const EPSILON = 1e-10

const cross = (origin, pointA, pointB) =>
  (pointA[0] - origin[0]) * (pointB[1] - origin[1]) -
  (pointA[1] - origin[1]) * (pointB[0] - origin[0])

const onSegment = (start, point, end) =>
  Math.min(start[0], end[0]) - EPSILON <= point[0] &&
  point[0] <= Math.max(start[0], end[0]) + EPSILON &&
  Math.min(start[1], end[1]) - EPSILON <= point[1] &&
  point[1] <= Math.max(start[1], end[1]) + EPSILON

const segmentsIntersect = (p1, p2, p3, p4) => {
  const d1 = cross(p3, p4, p1)
  const d2 = cross(p3, p4, p2)
  const d3 = cross(p1, p2, p3)
  const d4 = cross(p1, p2, p4)

  if (
    ((d1 > EPSILON && d2 < -EPSILON) || (d1 < -EPSILON && d2 > EPSILON)) &&
    ((d3 > EPSILON && d4 < -EPSILON) || (d3 < -EPSILON && d4 > EPSILON))
  ) {
    return true
  }

  if (Math.abs(d1) <= EPSILON && onSegment(p3, p1, p4)) return true
  if (Math.abs(d2) <= EPSILON && onSegment(p3, p2, p4)) return true
  if (Math.abs(d3) <= EPSILON && onSegment(p1, p3, p2)) return true
  if (Math.abs(d4) <= EPSILON && onSegment(p1, p4, p2)) return true

  return false
}

const arePointsEqual = (pointA, pointB) =>
  Math.abs(pointA[0] - pointB[0]) <= EPSILON &&
  Math.abs(pointA[1] - pointB[1]) <= EPSILON

const closeRing = ring => {
  if (!ring?.length) return []
  const closed = ring.map(point => [...point])
  const first = closed[0]
  const last = closed[closed.length - 1]
  if (!arePointsEqual(first, last)) {
    closed.push([...first])
  }
  return closed
}

export const isPolygonRingClosed = ring => {
  if (!ring?.length) return false
  return arePointsEqual(ring[0], ring[ring.length - 1])
}

export const hasRingSelfIntersection = ring => {
  const closedRing = closeRing(ring)
  const pointCount = closedRing.length
  if (pointCount < 4) return false

  const edgeCount = pointCount - 1
  for (let i = 0; i < edgeCount; i++) {
    const edgeStart = closedRing[i]
    const edgeEnd = closedRing[i + 1]

    for (let j = i + 1; j < edgeCount; j++) {
      if (j === i + 1) continue
      if (i === 0 && j === edgeCount - 1) continue

      const otherStart = closedRing[j]
      const otherEnd = closedRing[j + 1]
      if (segmentsIntersect(edgeStart, edgeEnd, otherStart, otherEnd)) {
        return true
      }
    }
  }

  return false
}

const getUniqueVertexCount = ring => {
  if (!ring?.length) return 0
  const isClosed = arePointsEqual(ring[0], ring[ring.length - 1])
  return isClosed ? ring.length - 1 : ring.length
}

export const isPointInRing = (point, ring) => {
  if (!point || !ring?.length) return false

  const [x, y] = point
  const vertexCount = getUniqueVertexCount(ring)
  let inside = false

  for (let i = 0, j = vertexCount - 1; i < vertexCount; j = i++) {
    const [xi, yi] = ring[i]
    const [xj, yj] = ring[j]
    const intersects =
      yi > y !== yj > y &&
      x < ((xj - xi) * (y - yi)) / (yj - yi) + xi

    if (intersects) {
      inside = !inside
    }
  }

  return inside
}

export const pointsToGeoJson = points => {
  if (!points?.length || points.length < 3) return null

  const coords = points.map(point =>
    Array.isArray(point) ? [...point] : [point.lng, point.lat]
  )

  return {
    type: "Polygon",
    coordinates: [closeRing(coords)],
  }
}

/**
 * Normalize any point-like value to plain numeric { lat, lng }.
 * Google Maps LatLng exposes .lat/.lng as functions — passing those objects
 * into geometry.poly.containsLocation can throw
 * "not a LatLng or LatLngLiteral: in property lat: not a number".
 */
export const toLatLngLiteral = point => {
  if (!point) return null

  if (typeof point.lat === "function" && typeof point.lng === "function") {
    const lat = Number(point.lat())
    const lng = Number(point.lng())
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return { lat, lng }
    }
    return null
  }

  if (Array.isArray(point) && point.length >= 2 && !Array.isArray(point[0])) {
    const lat = Number(point[1])
    const lng = Number(point[0])
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return { lat, lng }
    }
    return null
  }

  if (typeof point === "object") {
    const lat = Number(
      typeof point.lat === "function"
        ? point.lat()
        : (point.lat ?? point.latitude)
    )
    const lng = Number(
      typeof point.lng === "function"
        ? point.lng()
        : (point.lng ?? point.longitude)
    )
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return { lat, lng }
    }
  }

  return null
}

/** Normalize a GeoJSON ring to numeric [lng, lat] pairs. */
export const toNumericRing = coordinatesRing => {
  if (!Array.isArray(coordinatesRing) || !coordinatesRing.length) {
    return []
  }

  return coordinatesRing
    .map(point => {
      const literal = toLatLngLiteral(point)
      return literal ? [literal.lng, literal.lat] : null
    })
    .filter(Boolean)
}

/**
 * Convert a GeoJSON polygon ring to Google Maps LatLngLiterals.
 * Accepts [lng, lat] arrays or {lat,lng}/{latitude,longitude} objects,
 * coerces numeric strings, and drops invalid points.
 */
export const toLatLngPaths = coordinatesRing => {
  if (!Array.isArray(coordinatesRing) || !coordinatesRing.length) {
    return []
  }

  return coordinatesRing.map(toLatLngLiteral).filter(Boolean)
}

const hasBoundary = zone =>
  Boolean(zone?.boundary?.coordinates?.[0]?.length)

const shouldCompareZone = (zone, excludeZoneId) => {
  if (!hasBoundary(zone)) return false
  if (excludeZoneId && String(zone._id) === String(excludeZoneId)) return false
  return true
}

export const ringsOverlap = (ringA, ringB) => {
  const numericA = toNumericRing(ringA)
  const numericB = toNumericRing(ringB)
  if (numericA.length < 3 || numericB.length < 3) return false

  const closedA = closeRing(numericA)
  const closedB = closeRing(numericB)
  const vertexCountA = getUniqueVertexCount(closedA)
  const vertexCountB = getUniqueVertexCount(closedB)

  for (let i = 0; i < vertexCountA; i++) {
    if (isPointInRing(closedA[i], closedB)) return true
  }

  for (let i = 0; i < vertexCountB; i++) {
    if (isPointInRing(closedB[i], closedA)) return true
  }

  const edgeCountA = closedA.length - 1
  const edgeCountB = closedB.length - 1

  for (let i = 0; i < edgeCountA; i++) {
    const midpointA = [
      (closedA[i][0] + closedA[i + 1][0]) / 2,
      (closedA[i][1] + closedA[i + 1][1]) / 2,
    ]
    if (isPointInRing(midpointA, closedB)) return true
  }

  for (let i = 0; i < edgeCountB; i++) {
    const midpointB = [
      (closedB[i][0] + closedB[i + 1][0]) / 2,
      (closedB[i][1] + closedB[i + 1][1]) / 2,
    ]
    if (isPointInRing(midpointB, closedA)) return true
  }

  for (let i = 0; i < edgeCountA; i++) {
    for (let j = 0; j < edgeCountB; j++) {
      if (
        segmentsIntersect(
          closedA[i],
          closedA[i + 1],
          closedB[j],
          closedB[j + 1]
        )
      ) {
        return true
      }
    }
  }

  return false
}

export const boundariesOverlap = (boundaryA, boundaryB) => {
  const ringA = boundaryA?.coordinates?.[0]
  const ringB = boundaryB?.coordinates?.[0]
  if (!ringA?.length || !ringB?.length) return false
  return ringsOverlap(ringA, ringB)
}

export const isPointInsideExistingZones = (
  latLng,
  existingZones = [],
  excludeZoneId = null
) => {
  const pointLiteral = toLatLngLiteral(latLng)
  if (!pointLiteral) return null

  const point = [pointLiteral.lng, pointLiteral.lat]

  for (const zone of existingZones) {
    if (!shouldCompareZone(zone, excludeZoneId)) continue

    const ring = toNumericRing(zone.boundary?.coordinates?.[0])
    if (ring.length >= 3 && isPointInRing(point, ring)) {
      return zone
    }
  }

  return null
}

/**
 * Pure-JS overlap check. Do not use Google geometry here — containsLocation
 * logs InvalidValueError when Maps was loaded more than once or when LatLng
 * instances are cross-checked against LatLngLiteral validators.
 */
export const findOverlappingBoundary = (
  boundary,
  existingZones = [],
  excludeZoneId = null
) => {
  if (!boundary?.coordinates?.[0]?.length) return null

  for (const zone of existingZones) {
    if (!shouldCompareZone(zone, excludeZoneId)) continue
    if (boundariesOverlap(boundary, zone.boundary)) {
      return zone
    }
  }

  return null
}
